// Public "request account deletion" page, required by the app stores.
//
// Deliberately OUTSIDE the authenticated area and NOT in the sidebar: a user
// who has already lost access still has to be able to reach it, and the store
// reviewer opens it as a plain URL with no login.
//
// It asks for the same three things the login page does - company code,
// username, password - because the request must be proven to come from the
// account owner. Without that check anyone who knew a username could close
// somebody else's account.
import React, { useState } from 'react';
import {
  Alert, Box, Button, CircularProgress, Paper, TextField, Typography,
} from '@mui/material';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import config from '../config';

export default function DeleteAccount() {
  const [companyCode, setCompanyCode] = useState('');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [reason, setReason] = useState('');

  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError('');

    if (!companyCode.trim() || !username.trim() || !password) {
      setError('Please fill in the company code, username and password.');
      return;
    }

    setBusy(true);
    try {
      // Resolve the company exactly the way the login page does, so the code the
      // user already knows is the code that works here too.
      const initRes = await fetch(
        `${config.API_BASE_URL}/account/init?client=${encodeURIComponent(companyCode.trim())}`,
      );
      if (!initRes.ok) {
        setError('Company code not recognised. Please check and try again.');
        return;
      }
      const company = await initRes.json();
      if (!company?.id) {
        setError('Company code not recognised. Please check and try again.');
        return;
      }

      const res = await fetch(`${config.API_BASE_URL}/accountdeletion/request`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          Username: username.trim(),
          Password: password,
          Companyid: company.id,
          Reason: reason.trim(),
        }),
      });

      const body = await res.json().catch(() => null);
      if (!res.ok) {
        setError(body?.message || 'Could not submit the request. Please try again.');
        return;
      }
      setDone(true);
    } catch {
      setError('Network error. Please check your connection and try again.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        bgcolor: '#F1F5F9',
        p: 2,
      }}>
      <Paper
        elevation={0}
        sx={{
          width: '100%',
          maxWidth: 460,
          p: { xs: 3, sm: 4 },
          borderRadius: '16px',
          border: '1px solid #E2E8F0',
        }}>
        {done ? (
          // Confirmation. The wording is the whole point of the page for a store
          // reviewer, so it is plain and unambiguous.
          <Box sx={{ textAlign: 'center', py: 2 }}>
            <DeleteForeverIcon sx={{ fontSize: 48, color: '#B45309', mb: 1 }} />
            <Typography sx={{ fontSize: '1.15rem', fontWeight: 800, mb: 1.5 }}>
              Request submitted
            </Typography>
            <Alert severity='success' sx={{ textAlign: 'left' }}>
              Your account deletion request has been submitted. Your account has
              been closed and you can no longer sign in. Your data will be
              removed by our team.
            </Alert>
          </Box>
        ) : (
          <form onSubmit={submit}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
              <DeleteForeverIcon sx={{ color: '#B45309' }} />
              <Typography sx={{ fontSize: '1.15rem', fontWeight: 800 }}>
                Delete My Account
              </Typography>
            </Box>
            <Typography sx={{ fontSize: '0.85rem', color: '#64748B', mb: 2.5 }}>
              Sign in with your usual details to confirm this is your account.
            </Typography>

            {error && <Alert severity='error' sx={{ mb: 2 }}>{error}</Alert>}

            <TextField
              fullWidth size='small' label='Company Code' required
              value={companyCode}
              onChange={(e) => setCompanyCode(e.target.value)}
              autoComplete='organization'
              sx={{ mb: 2 }}
            />
            <TextField
              fullWidth size='small' label='Username' required
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              autoComplete='username'
              sx={{ mb: 2 }}
            />
            <TextField
              fullWidth size='small' label='Password' type='password' required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete='current-password'
              sx={{ mb: 2 }}
            />
            <TextField
              fullWidth size='small' label='Reason (optional)'
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              multiline minRows={2}
              sx={{ mb: 3 }}
            />

            <Button
              type='submit'
              fullWidth
              variant='contained'
              color='error'
              disabled={busy}
              startIcon={busy ? <CircularProgress size={16} color='inherit' /> : <DeleteForeverIcon />}
              sx={{ textTransform: 'none', fontWeight: 700, py: 1.1 }}>
              {busy ? 'Submitting…' : 'Request to delete my account and data'}
            </Button>
          </form>
        )}
      </Paper>
    </Box>
  );
}